import { useState, useRef } from 'react';
import {
  Box, Container, Grid, Typography,
  Chip, Stack, Button, List, ListItem, ListItemIcon, ListItemText, CircularProgress,
  Collapse, Divider,
} from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { motion, useInView } from 'framer-motion';
import PageHero from '../components/common/PageHero';
import { Link as RouterLink } from 'react-router-dom';
import CheckIcon from '@mui/icons-material/Check';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import WorkIcon from '@mui/icons-material/Work';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CTABanner from '../components/common/CTABanner';
import { useJobs } from '../hooks/usePublicApi';
import { jobs as staticJobs, companyBenefits as staticBenefits, teamValues } from '../data/jobs';

const navy  = '#0F1D2E';
const navyL = '#1B2D42';
const gold  = '#D1BC8A';
const goldD = '#B8A06E';

const benefitEmojis = ['💰', '🚗', '📚', '👥', '🛡️', '⏰'];

// ── Blueprint overlay ─────────────────────────────────────────────────────────
function Blueprint({ opacity = 0.06 }) {
  return (
    <Box sx={{
      position: 'absolute', inset: 0, pointerEvents: 'none',
      backgroundImage: `
        linear-gradient(rgba(255,255,255,${opacity}) 1px, transparent 1px),
        linear-gradient(90deg, rgba(255,255,255,${opacity}) 1px, transparent 1px)
      `,
      backgroundSize: '36px 36px',
    }} />
  );
}

// ── Scroll-triggered fade-up ──────────────────────────────────────────────────
function InView({ children, delay = 0, style }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-70px 0px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{ duration: 0.65, delay, ease: [0.22, 1, 0.36, 1] }}
      style={{ height: '100%', ...style }}
    >
      {children}
    </motion.div>
  );
}

// ── Expandable job card ───────────────────────────────────────────────────────
function JobCard({ job, defaultExpanded = false }) {
  const [open, setOpen] = useState(defaultExpanded);
  const Icon = job.icon;

  return (
    <motion.div
      whileHover={!open ? { y: -3 } : {}}
      transition={{ duration: 0.3 }}
    >
      <Box
        sx={{
          borderRadius: '18px',
          overflow: 'hidden',
          background: '#fff',
          boxShadow: open
            ? '0 12px 40px rgba(15,29,46,0.12)'
            : '0 2px 16px rgba(15,29,46,0.07)',
          border: `1.5px solid ${open ? 'rgba(209,188,138,0.35)' : 'rgba(15,29,46,0.07)'}`,
          transition: 'all 0.35s ease',
        }}
      >
        {/* Header */}
        <Box
          onClick={() => setOpen(!open)}
          sx={{
            p: { xs: 2.5, md: 3 },
            display: 'flex', alignItems: 'center', gap: 2.5,
            cursor: 'pointer',
            '&:hover': { bgcolor: 'rgba(209,188,138,0.04)' },
            transition: 'background 0.25s ease',
          }}
        >
          {/* Icon badge */}
          <Box sx={{
            width: 52, height: 52, borderRadius: '14px', flexShrink: 0,
            background: open
              ? `linear-gradient(135deg, ${gold} 0%, ${goldD} 100%)`
              : 'linear-gradient(135deg, rgba(209,188,138,0.12) 0%, rgba(209,188,138,0.06) 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'all 0.3s ease',
          }}>
            {Icon
              ? <Icon sx={{ fontSize: 24, color: open ? navy : gold }} />
              : <WorkIcon sx={{ fontSize: 24, color: open ? navy : gold }} />
            }
          </Box>

          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: navy, mb: 0.5, lineHeight: 1.3 }}>
              {job.title}
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" gap={0.5}>
              <Chip
                label={job.type}
                size="small"
                sx={{ bgcolor: 'rgba(209,188,138,0.14)', color: '#8B6914', fontWeight: 700, fontSize: '0.72rem' }}
              />
              {job.location && (
                <Stack direction="row" spacing={0.4} alignItems="center">
                  <LocationOnIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary">{job.location}</Typography>
                </Stack>
              )}
            </Stack>
          </Box>

          <Box sx={{
            width: 36, height: 36, borderRadius: '50%',
            bgcolor: open ? navy : 'rgba(15,29,46,0.06)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
            transition: 'all 0.3s ease',
          }}>
            <ExpandMoreIcon sx={{
              fontSize: 20,
              color: open ? gold : 'text.secondary',
              transform: open ? 'rotate(180deg)' : 'rotate(0)',
              transition: 'transform 0.3s ease',
            }} />
          </Box>
        </Box>

        {/* Expandable body */}
        <Collapse in={open}>
          <Divider sx={{ borderColor: 'rgba(209,188,138,0.2)' }} />
          <Box sx={{ p: { xs: 2.5, md: 3 } }}>
            <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.8, mb: 3 }}>
              {job.description}
            </Typography>

            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: navy }}>
                  Anforderungen
                </Typography>
                <List dense disablePadding>
                  {(job.requirements || []).map((req, i) => (
                    <ListItem key={i} disableGutters sx={{ py: 0.4, alignItems: 'flex-start' }}>
                      <ListItemIcon sx={{ minWidth: 26, mt: 0.1 }}>
                        <Box sx={{
                          width: 18, height: 18, borderRadius: '50%',
                          bgcolor: 'rgba(209,188,138,0.18)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          <CheckIcon sx={{ fontSize: 11, color: goldD }} />
                        </Box>
                      </ListItemIcon>
                      <ListItemText
                        primary={typeof req === 'string' ? req : req.requirement_text}
                        primaryTypographyProps={{ variant: 'body2', sx: { lineHeight: 1.55 } }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: navy }}>
                  Wir bieten
                </Typography>
                <List dense disablePadding>
                  {(job.benefits || []).map((ben, i) => (
                    <ListItem key={i} disableGutters sx={{ py: 0.4, alignItems: 'flex-start' }}>
                      <ListItemIcon sx={{ minWidth: 26, mt: 0.1 }}>
                        <Box sx={{
                          width: 18, height: 18, borderRadius: '50%',
                          background: `linear-gradient(135deg, ${gold} 0%, ${goldD} 100%)`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          <CheckIcon sx={{ fontSize: 11, color: navy }} />
                        </Box>
                      </ListItemIcon>
                      <ListItemText
                        primary={typeof ben === 'string' ? ben : ben.benefit_text}
                        primaryTypographyProps={{ variant: 'body2', sx: { lineHeight: 1.55 } }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Grid>
            </Grid>

            <Button
              variant="contained"
              component={RouterLink}
              to={`/anfrage?type=job&position=${encodeURIComponent(job.title)}`}
              endIcon={<ArrowForwardIcon />}
              sx={{
                background: `linear-gradient(135deg, ${gold} 0%, ${goldD} 100%)`,
                color: navy, fontWeight: 800,
                borderRadius: '12px', px: 3, py: 1.3,
                boxShadow: `0 6px 20px rgba(209,188,138,0.28)`,
                '&:hover': { transform: 'translateY(-2px)', boxShadow: `0 12px 32px rgba(209,188,138,0.38)` },
                transition: 'all 0.3s ease',
              }}
            >
              Jetzt bewerben
            </Button>
          </Box>
        </Collapse>
      </Box>
    </motion.div>
  );
}

// ── Benefit card ──────────────────────────────────────────────────────────────
function BenefitCard({ benefit, index }) {
  return (
    <Box sx={{
      p: 3, borderRadius: '18px',
      background: '#fff',
      border: '1px solid rgba(15,29,46,0.07)',
      boxShadow: '0 2px 12px rgba(15,29,46,0.05)',
      height: '100%',
      transition: 'all 0.3s ease',
      '&:hover': {
        borderColor: 'rgba(209,188,138,0.35)',
        boxShadow: '0 8px 28px rgba(15,29,46,0.1)',
        transform: 'translateY(-4px)',
      },
    }}>
      <Typography sx={{ fontSize: '2rem', mb: 1.5, lineHeight: 1 }}>
        {benefit.icon || benefitEmojis[index % benefitEmojis.length]}
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 700, color: navy, mb: 0.75 }}>
        {benefit.title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65 }}>
        {benefit.description}
      </Typography>
    </Box>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function Karriere() {
  const { jobs: apiJobs, companyBenefits: apiBenefits, loading } = useJobs();
  const jobs = apiJobs || staticJobs;
  const companyBenefits = apiBenefits || staticBenefits;

  return (
    <>
      <Helmet>
        <title>Karriere — Jobs bei Süd Ausbau GmbH</title>
        <meta name="description" content="Werden Sie Teil des Teams der Süd Ausbau GmbH. Aktuelle Stellenangebote für Trockenbaumonteure, Bauleiter und mehr." />
      </Helmet>

      <PageHero overline="Karriere" title="Werden Sie Teil unseres Teams" />

      {/* ── Team values strip ────────────────────────────────────────────────── */}
      <Box sx={{ bgcolor: navy, borderTop: '1px solid rgba(209,188,138,0.1)', borderBottom: '1px solid rgba(209,188,138,0.1)', py: 5 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {(teamValues || []).map((val, i) => {
              const ValIcon = val.icon;
              return (
                <Grid size={{ xs: 12, sm: 4 }} key={i}>
                  <InView delay={i * 0.1} style={{ height: 'auto' }}>
                    <Stack direction="row" spacing={2} alignItems="flex-start">
                      <Box sx={{
                        width: 44, height: 44, borderRadius: '12px', flexShrink: 0,
                        bgcolor: 'rgba(209,188,138,0.12)',
                        border: '1px solid rgba(209,188,138,0.2)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <ValIcon sx={{ fontSize: 22, color: gold }} />
                      </Box>
                      <Box>
                        <Typography sx={{ color: '#fff', fontWeight: 700, mb: 0.4 }}>{val.title}</Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.52)', lineHeight: 1.65 }}>
                          {val.description}
                        </Typography>
                      </Box>
                    </Stack>
                  </InView>
                </Grid>
              );
            })}
          </Grid>
        </Container>
      </Box>

      {/* ── Job listings ─────────────────────────────────────────────────────── */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: '#F8F7F5' }}>
        <Container maxWidth="md">
          <InView style={{ height: 'auto' }}>
            <Box sx={{ mb: { xs: 5, md: 7 } }}>
              <Typography sx={{ color: gold, fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.2em', mb: 0.75 }}>
                Offene Stellen
              </Typography>
              <Typography variant="h2" sx={{ color: navy, fontWeight: 900, lineHeight: 1.08, letterSpacing: '-0.025em' }}>
                Aktuelle Stellenangebote
              </Typography>
            </Box>
          </InView>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress sx={{ color: gold }} />
            </Box>
          ) : (
            <Stack spacing={2}>
              {jobs.map((job, i) => (
                <InView key={job.id} delay={i * 0.08} style={{ height: 'auto' }}>
                  <JobCard job={job} defaultExpanded={i === 0} />
                </InView>
              ))}
            </Stack>
          )}
        </Container>
      </Box>

      {/* ── Benefits ─────────────────────────────────────────────────────────── */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: '#fff' }}>
        <Container maxWidth="lg">
          <InView style={{ height: 'auto' }}>
            <Box sx={{ mb: { xs: 5, md: 7 } }}>
              <Typography sx={{ color: gold, fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.2em', mb: 0.75 }}>
                Benefits
              </Typography>
              <Typography variant="h2" sx={{ color: navy, fontWeight: 900, lineHeight: 1.08, letterSpacing: '-0.025em' }}>
                Was wir bieten
              </Typography>
            </Box>
          </InView>

          <Grid container spacing={2.5}>
            {companyBenefits.map((benefit, i) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={i}>
                <InView delay={i * 0.08} style={{ height: 'auto' }}>
                  <BenefitCard benefit={benefit} index={i} />
                </InView>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <CTABanner
        pageKey="karriere"
        title="Keine passende Stelle dabei?"
        subtitle="Wir freuen uns auch über Initiativbewerbungen. Senden Sie uns Ihre Unterlagen!"
        primaryAction={{ label: 'Initiativbewerbung', to: '/anfrage?type=job&position=Initiativbewerbung' }}
      />
    </>
  );
}
